import type { ComponentProps, ReactElement, ReactNode } from "react";
import { Lock, type LucideIcon } from "lucide-react";
import type { PageName } from "../../navigation/pages";
import { routePath, type AppRoute } from "../../navigation/routes";
import { AppLink } from "../navigation/AppLink";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { cn } from "../ui/utils";

interface RailDestinationProps {
    page: PageName;
    label: string;
    Icon: LucideIcon;
    collapsed: boolean;
    /** The page on screen, and the deepest link this copy of the rail draws for it. */
    active: boolean;
    /** A place inside this page is on screen and marked below it: never tinted, never `aria-current`. */
    ancestor?: boolean;
    /** Locked until the property analysis has completed. */
    locked?: boolean;
    onSelect: () => void;
    /** Spread onto the link, its `ref` included. */
    linkProps?: Omit<ComponentProps<"a">, "href" | "onClick" | "children">;
    /** Stands in for the tooltip the folded link explains itself in. */
    renderFolded?: (link: ReactElement) => ReactNode;
}

/**
 * One destination on the rail: a real link to its page, so it can be opened
 * in a new tab, bookmarked or copied like a link anywhere else — or, while
 * locked, a disabled button, since a link to a page nobody may open yet would
 * lead nowhere.
 */
export function RailDestination({
    page,
    label,
    Icon,
    collapsed,
    active,
    ancestor = false,
    locked = false,
    onSelect,
    linkProps,
    renderFolded,
}: RailDestinationProps) {
    const className = cn(
        "w-full h-10 sm:h-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        collapsed ? "justify-center px-2" : "justify-start",
        // No tint for the ancestor: in some skins the rail's tint is its "selected" pill and outranks the current chip.
        active ? "bg-accent text-primary" : ancestor && "font-medium",
        locked && "opacity-50",
    );
    const content = (
        <>
            <Icon className={`h-4 w-4 ${collapsed ? "" : "mr-3"} flex-shrink-0`} />
            {!collapsed && <span className="truncate flex-1 text-left">{label}</span>}
            {!collapsed && locked && <Lock className="h-3.5 w-3.5 ml-auto text-sidebar-foreground/40 flex-shrink-0" />}
        </>
    );

    if (locked) {
        return (
            <Button
                variant="ghost"
                className={className}
                title={collapsed ? label : undefined}
                aria-label={collapsed ? label : undefined}
                disabled
            >
                {content}
            </Button>
        );
    }

    const link = (
        <Button asChild variant="ghost" className={className}>
            <AppLink
                {...linkProps}
                to={routePath({ page } as AppRoute)}
                active={active}
                onNavigate={onSelect}
                aria-label={collapsed ? label : undefined}
            >
                {content}
            </AppLink>
        </Button>
    );

    if (!collapsed) return link;
    if (renderFolded) return renderFolded(link);

    // Hover and keyboard focus both open it, which a native title never did.
    return (
        <Tooltip>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
    );
}
